import { Helmet } from "react-helmet-async";
import { ArrowRight } from "lucide-react";

/**
 * Preview: homepage content reskinned in the "CSW Canada" pitch-deck aesthetic.
 * Cream background, Cormorant Garamond display italic headlines, Jost body,
 * hairline rules, eyebrow labels, single rust signal accent.
 * Fully self-contained — no shared tokens touched.
 */

const cream = "#F6F1E7";
const ink = "#1C1A17";
const warm = "#8A8479";
const rule = "#D9D2C3";
const signal = "#C2562A";

const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <span
    style={{
      fontFamily: "'Jost', sans-serif",
      fontSize: "0.7rem",
      letterSpacing: "0.32em",
      textTransform: "uppercase",
      color: warm,
      fontWeight: 400,
    }}
  >
    {children}
  </span>
);

const Hairline = ({ width = "100%" }: { width?: string }) => (
  <div style={{ height: 1, background: rule, width }} />
);

const HomeCanadaPreview = () => {
  const display: React.CSSProperties = {
    fontFamily: "'Cormorant Garamond', serif",
    fontWeight: 400,
    letterSpacing: "-0.01em",
    color: ink,
  };
  const body: React.CSSProperties = {
    fontFamily: "'Jost', sans-serif",
    fontWeight: 300,
    color: ink,
    letterSpacing: "0.01em",
  };

  return (
    <div style={{ background: cream, minHeight: "100vh", ...body }}>
      <Helmet>
        <title>Home (Canada Aesthetic Preview) — Study Stream</title>
        <link
          href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400;1,500&family=Jost:wght@300;400;500&display=swap"
          rel="stylesheet"
        />
        <meta name="robots" content="noindex" />
      </Helmet>

      {/* Top frame bar */}
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          padding: "1.75rem 2.5rem",
          fontFamily: "'Jost', sans-serif",
          fontSize: "0.7rem",
          letterSpacing: "0.32em",
          textTransform: "uppercase",
          color: warm,
        }}
      >
        <span>CSW — Study Stream</span>
        <span>Body Doubling · Est. 2024</span>
      </header>

      {/* HERO */}
      <section style={{ padding: "3rem 2.5rem 6rem", maxWidth: 1440, margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: "5rem", alignItems: "center" }}>
          <article>
            <Eyebrow>Free forever — no limits</Eyebrow>
            <h1
              style={{
                ...display,
                fontSize: "clamp(3.5rem, 7vw, 6.5rem)",
                lineHeight: 0.95,
                margin: "1.5rem 0 0",
              }}
            >
              Study <em style={{ color: signal }}>together.</em>
              <br />
              Focus <em>together.</em>
            </h1>

            <div style={{ margin: "2.5rem 0 1.75rem", width: 64 }}>
              <Hairline />
            </div>

            <p style={{ ...body, fontSize: "1.1rem", lineHeight: 1.6, maxWidth: 520, color: "#3a3833" }}>
              CSW is a free body-doubling platform where you study alongside others on camera. Join live
              study streams, "study with me" sessions, and virtual rooms with people around the world —
              because you focus better when you're not alone.
            </p>

            <div style={{ display: "flex", gap: "1rem", marginTop: "2.5rem", alignItems: "center" }}>
              <a
                href="https://app.csw.live"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  ...body,
                  background: ink,
                  color: cream,
                  padding: "0.95rem 1.6rem",
                  fontSize: "0.78rem",
                  letterSpacing: "0.28em",
                  textTransform: "uppercase",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.6rem",
                  textDecoration: "none",
                }}
              >
                Start studying now <ArrowRight size={14} />
              </a>
              <a
                href="https://app.csw.live"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  ...body,
                  color: ink,
                  padding: "0.95rem 0.4rem",
                  fontSize: "0.78rem",
                  letterSpacing: "0.28em",
                  textTransform: "uppercase",
                  borderBottom: `1px solid ${ink}`,
                  textDecoration: "none",
                }}
              >
                Try our beta
              </a>
            </div>
          </article>

          {/* Data card mimicking the pitch deck classroom panel */}
          <aside
            style={{
              border: `1px solid ${rule}`,
              padding: "1.75rem",
              background: cream,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "1.5rem" }}>
              <Eyebrow>Live Stream</Eyebrow>
              <Eyebrow>Now</Eyebrow>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 0 }}>
              {[
                { label: "Sessions", value: "10,438" },
                { label: "Rooms", value: "24/7" },
                { label: "Cost", value: "$0" },
              ].map((s, i) => (
                <div
                  key={s.label}
                  style={{
                    border: `1px solid ${rule}`,
                    marginLeft: i === 0 ? 0 : -1,
                    padding: "1rem 1.1rem",
                  }}
                >
                  <Eyebrow>{s.label}</Eyebrow>
                  <div style={{ ...display, fontSize: "1.65rem", marginTop: "0.35rem" }}>{s.value}</div>
                </div>
              ))}
            </div>

            {/* bar chart */}
            <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 140, marginTop: "1.75rem" }}>
              {[42, 28, 60, 48, 22, 50, 95, 54, 38, 70].map((h, i) => (
                <div
                  key={i}
                  style={{
                    flex: 1,
                    height: `${h}%`,
                    background: i === 6 ? signal : "#C8C1B0",
                  }}
                />
              ))}
            </div>
            <div style={{ marginTop: "1rem" }}>
              <Hairline />
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: "0.6rem" }}>
                <Eyebrow>Focus index</Eyebrow>
                <Eyebrow>94%</Eyebrow>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* STUDY STREAM */}
      <section style={{ padding: "5rem 2.5rem", maxWidth: 1440, margin: "0 auto" }}>
        <Hairline />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: "4rem", paddingTop: "3.5rem" }}>
          <div>
            <Eyebrow>01 — The Stream</Eyebrow>
          </div>
          <div>
            <h2 style={{ ...display, fontSize: "clamp(2.5rem, 4.5vw, 4rem)", lineHeight: 1.02, margin: 0 }}>
              A <em style={{ color: signal }}>study stream</em> is a quiet room
              <br />
              with a thousand other minds inside it.
            </h2>
            <div style={{ margin: "2.5rem 0", width: 64 }}>
              <Hairline />
            </div>
            <p style={{ fontSize: "1.05rem", lineHeight: 1.7, maxWidth: 640, color: "#3a3833" }}>
              Turn your camera on. Sit down. Open the book you've been avoiding. Around you, students from
              Seoul, São Paulo, and Stockholm are doing the same thing. No chatter. No pressure. Just the
              soft accountability of being seen.
            </p>
          </div>
        </div>
      </section>

      {/* WITH ME */}
      <section style={{ padding: "5rem 2.5rem", maxWidth: 1440, margin: "0 auto" }}>
        <Hairline />
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "4rem", paddingTop: "3.5rem" }}>
          <div>
            <Eyebrow>02 — Study With Me</Eyebrow>
            <h2 style={{ ...display, fontSize: "clamp(2.5rem, 4.5vw, 4rem)", lineHeight: 1.02, margin: "1rem 0 0" }}>
              The oldest productivity hack,
              <br />
              <em>reimagined for the browser.</em>
            </h2>
          </div>
          <div style={{ alignSelf: "end" }}>
            <p style={{ fontSize: "1rem", lineHeight: 1.7, color: "#3a3833" }}>
              Body doubling has helped writers, monks, and ADHD brains for centuries. We just put it on the
              web — and made it free.
            </p>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", marginTop: "3rem" }}>
          {[
            { n: "I.", t: "Show up", d: "Open a tab. Pick a room. Your camera does the rest." },
            { n: "II.", t: "Stay seated", d: "The presence of strangers is enough to keep you grounded." },
            { n: "III.", t: "Finish the thing", d: "Leave with the same satisfaction as a library closing." },
          ].map((c, i) => (
            <div
              key={c.t}
              style={{
                borderLeft: i === 0 ? `1px solid ${rule}` : "none",
                borderRight: `1px solid ${rule}`,
                borderTop: `1px solid ${rule}`,
                borderBottom: `1px solid ${rule}`,
                padding: "2rem 1.75rem 2.5rem",
              }}
            >
              <div style={{ ...display, fontStyle: "italic", color: signal, fontSize: "1.5rem" }}>{c.n}</div>
              <h3 style={{ ...display, fontSize: "1.75rem", margin: "0.5rem 0 1rem" }}>{c.t}</h3>
              <p style={{ fontSize: "0.95rem", lineHeight: 1.65, color: "#3a3833" }}>{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SCIENCE / BODY DOUBLING */}
      <section style={{ padding: "5rem 2.5rem", maxWidth: 1440, margin: "0 auto" }}>
        <Hairline />
        <div style={{ paddingTop: "3.5rem", textAlign: "center", maxWidth: 880, margin: "0 auto" }}>
          <Eyebrow>03 — The Science</Eyebrow>
          <h2 style={{ ...display, fontSize: "clamp(2.5rem, 5vw, 4.25rem)", lineHeight: 1.05, margin: "1.25rem 0 0" }}>
            Body doubling — <em style={{ color: signal }}>the quiet</em>
            <br /> <em style={{ color: signal }}>physics</em> of getting things done.
          </h2>
          <div style={{ margin: "2.5rem auto", width: 64 }}>
            <Hairline />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", marginTop: "2rem" }}>
          {[
            { k: "30%", v: "Higher task completion when working in shared focus space." },
            { k: "2.4×", v: "More likely to finish a session that started with another person present." },
            { k: "94%", v: "Of users report deeper focus inside a live study room." },
            { k: "0", v: "Dollars. Forever. No tier, no trial, no card." },
          ].map((s, i) => (
            <div key={i} style={{ borderLeft: `1px solid ${rule}`, padding: "1.5rem 1.5rem 0" }}>
              <div style={{ ...display, fontSize: "3.25rem", lineHeight: 1, color: ink }}>{s.k}</div>
              <p style={{ marginTop: "1rem", fontSize: "0.9rem", lineHeight: 1.6, color: warm }}>{s.v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TOGETHER */}
      <section style={{ padding: "5rem 2.5rem", maxWidth: 1440, margin: "0 auto" }}>
        <Hairline />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4rem", paddingTop: "3.5rem" }}>
          <div>
            <Eyebrow>04 — Together</Eyebrow>
            <h2 style={{ ...display, fontSize: "clamp(2.25rem, 4vw, 3.5rem)", lineHeight: 1.05, margin: "1rem 0 0" }}>
              A global library
              <br /> <em>that never closes.</em>
            </h2>
          </div>
          <div>
            <p style={{ fontSize: "1rem", lineHeight: 1.75, color: "#3a3833" }}>
              At 3 a.m. in Toronto, someone in Manila just sat down to study. At noon in Berlin, a student
              in Buenos Aires opens a tab. The room is never empty — and neither is your desk.
            </p>
            <ul style={{ marginTop: "1.5rem", padding: 0, listStyle: "none" }}>
              {["Live rooms, 24/7", "Camera-on accountability", "No chat noise, no algorithms", "Free, always"].map(
                (li) => (
                  <li
                    key={li}
                    style={{
                      borderTop: `1px solid ${rule}`,
                      padding: "0.85rem 0",
                      display: "flex",
                      justifyContent: "space-between",
                      fontFamily: "'Jost', sans-serif",
                      fontSize: "0.85rem",
                      letterSpacing: "0.06em",
                    }}
                  >
                    <span>{li}</span>
                    <span style={{ color: signal, fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}>
                      ✓
                    </span>
                  </li>
                )
              )}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: "6rem 2.5rem 5rem", maxWidth: 1440, margin: "0 auto" }}>
        <Hairline />
        <div style={{ paddingTop: "4rem", textAlign: "center" }}>
          <Eyebrow>The invitation</Eyebrow>
          <h2 style={{ ...display, fontSize: "clamp(3rem, 6vw, 5.5rem)", lineHeight: 1, margin: "1.5rem 0 2rem" }}>
            Ready to <em style={{ color: signal }}>focus together?</em>
          </h2>
          <a
            href="https://app.csw.live"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              ...body,
              background: ink,
              color: cream,
              padding: "1.1rem 2rem",
              fontSize: "0.78rem",
              letterSpacing: "0.32em",
              textTransform: "uppercase",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.6rem",
              textDecoration: "none",
            }}
          >
            Join a stream <ArrowRight size={14} />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer
        style={{
          borderTop: `1px solid ${rule}`,
          padding: "2rem 2.5rem",
          display: "flex",
          justifyContent: "space-between",
          fontFamily: "'Jost', sans-serif",
          fontSize: "0.7rem",
          letterSpacing: "0.32em",
          textTransform: "uppercase",
          color: warm,
        }}
      >
        <span>CSW — Study Stream</span>
        <span>Preview · Canada Aesthetic</span>
      </footer>
    </div>
  );
};

export default HomeCanadaPreview;
